import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, Upload, Image as ImageIcon, Music } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { reciterAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const ReciterManagement = () => {
  const { toast } = useToast();
  const [reciters, setReciters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReciter, setEditingReciter] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    order: 1
  });
  const [imageFile, setImageFile] = useState(null);
  const [flagFile, setFlagFile] = useState(null);

  useEffect(() => {
    loadReciters();
  }, []);

  const loadReciters = async () => {
    try {
      const response = await reciterAPI.getAll();
      setReciters(response.data);
    } catch (error) {
      console.error('Error loading reciters:', error);
      const errorMessage = error.message ? String(error.message) : 'Hafizlar yuklenirken hata olustu.';
      toast({
        title: 'Hata!',
        description: String(errorMessage),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (reciter = null) => {
    if (reciter) {
      setEditingReciter(reciter);
      setFormData({
        name: reciter.name,
        country: reciter.country,
        order: reciter.order
      });
    } else {
      setEditingReciter(null);
      setFormData({
        name: '',
        country: '',
        order: reciters.length + 1
      });
    }
    setImageFile(null);
    setFlagFile(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingReciter(null);
    setImageFile(null);
    setFlagFile(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'order' ? parseInt(value) : value
    }));
  };

  const generateSlug = (text) => {
    // Generate slug from Turkish text
    const slug = text
      .toLowerCase()
      .replace(/ş/g, 's')
      .replace(/ı/g, 'i')
      .replace(/ğ/g, 'g')
      .replace(/ü/g, 'u')
      .replace(/ö/g, 'o')
      .replace(/ç/g, 'c')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    
    console.log('[ReciterManagement] Generated slug:', text, '→', slug);
    return slug;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('slug', generateSlug(formData.name));
      formDataToSend.append('country', formData.country);
      formDataToSend.append('order', formData.order);

      if (imageFile) {
        formDataToSend.append('imageFile', imageFile);
      }
      
      if (flagFile) {
        formDataToSend.append('flagFile', flagFile);
      }

      if (editingReciter) {
        await reciterAPI.update(editingReciter.id, formDataToSend);
        toast({
          title: 'Basarili!',
          description: 'Hafiz basariyla guncellendi.'
        });
      } else {
        await reciterAPI.create(formDataToSend);
        toast({
          title: 'Basarili!',
          description: 'Hafiz basariyla eklendi.'
        });
      }
      handleCloseDialog();
      loadReciters();
    } catch (error) {
      console.error('Error saving reciter:', error);
      
      let errorMessage = 'Hafiz kaydedilirken hata olustu.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = String(error.message);
      }
      
      toast({
        title: 'Hata!',
        description: String(errorMessage),
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu hafizi silmek istediginizden emin misiniz?')) return;
    
    try {
      await reciterAPI.delete(id);
      toast({
        title: 'Basarili!',
        description: 'Hafiz basariyla silindi.'
      });
      loadReciters();
    } catch (error) {
      console.error('Error deleting reciter:', error);
      const errorMessage = error.message ? String(error.message) : 'Hafiz silinirken hata olustu.';
      toast({
        title: 'Hata!',
        description: String(errorMessage),
        variant: 'destructive'
      });
    }
  };

  // Drag-drop functionality removed as per user request
  // Order can now be manually set in the form

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#8B7355]">Yukleniyor...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#5C4033]">Hafiz Yonetimi</h2>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Yeni Hafiz Ekle
        </Button>
      </div>

      <div className="space-y-3">
        {reciters.map((reciter, index) => (
          <Card
            key={reciter.id}
            className="bg-white border-[#E8DCC8] p-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-10 h-10 bg-[#D5C4A1] rounded-full flex items-center justify-center text-[#5C4033] font-bold">
                  {reciter.order}
                </div>

                {reciter.imageUrl && (
                  <img
                    src={reciter.imageUrl}
                    alt={reciter.name}
                    className="w-16 h-16 object-cover rounded-full"
                  />
                )}

                <div className="flex-1">
                  <h3 className="font-bold text-[#3E3028]">{reciter.name}</h3>
                  <p className="text-sm text-[#8B7355]">
                    {reciter.country}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(reciter)}
                  className="text-[#8B7355] hover:bg-[#F5EFE7]"
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(reciter.id)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {reciters.length === 0 && (
        <Card className="bg-white border-[#E8DCC8] p-12">
          <div className="text-center">
            <p className="text-[#8B7355]">
              Henuz hafiz eklenmemis. Yeni hafiz eklemek icin yukardaki butona tiklayin.
            </p>
          </div>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#F9F6F1]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#5C4033]">
              {editingReciter ? 'Hafiz Duzenle' : 'Yeni Hafiz Ekle'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Hafız ekleme veya düzenleme formu
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Hafız Adı</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Fatih Çollak"
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Ülke</label>
              <Input
                name="country"
                value={formData.country}
                onChange={handleChange}
                required
                placeholder="Türkiye"
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Sira Numarasi</label>
              <Input
                name="order"
                type="number"
                value={formData.order}
                onChange={handleChange}
                required
                min="1"
                className="bg-white border-[#D5C4A1]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Ülke Bayrağı</label>
              <div className="flex items-center space-x-3">
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                  onChange={(e) => setFlagFile(e.target.files[0])}
                  className="bg-white border-[#D5C4A1] flex-1"
                />
                {flagFile && (
                  <img 
                    src={URL.createObjectURL(flagFile)} 
                    alt="Yeni Bayrak" 
                    className="w-12 h-8 object-cover rounded border-2 border-[#8B7355]"
                  />
                )}
                {editingReciter?.flagUrl && !flagFile && (
                  <img 
                    src={editingReciter.flagUrl.startsWith('http') ? editingReciter.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${editingReciter.flagUrl.replace('/uploads/', '/api/files/')}`} 
                    alt="Mevcut Bayrak" 
                    className="w-12 h-8 object-cover rounded border-2 border-[#D5C4A1]"
                  />
                )}
              </div>
              <p className="text-xs text-[#8B7355]">PNG, JPG, GIF veya WEBP formatında bayrak resmi seçin</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Profil Gorseli</label>
              <div className="flex items-center space-x-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setImageFile(e.target.files[0])}
                  required={!editingReciter}
                  className="bg-white border-[#D5C4A1]"
                />
                <ImageIcon className="h-5 w-5 text-[#8B7355]" />
              </div>
              {editingReciter && !imageFile && (
                <p className="text-xs text-[#8B7355]">Mevcut gorseli degistirmek icin yeni bir gorsel secin.</p>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handleCloseDialog}
                className="text-[#6B5744]"
              >
                <X className="h-4 w-4 mr-2" />
                Iptal
              </Button>
              <Button
                type="submit"
                className="bg-[#8B7355] hover:bg-[#6B5744] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingReciter ? 'Guncelle' : 'Kaydet'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReciterManagement;
